import { Network } from "@capacitor/network";
import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import {
  FileModel,
  FileResponseModel,
  OfflineRequestModel,
} from "src/app/store/models/file.model";
import { HttpService } from "../http-service/http-client.service";
import { ActionModel } from "src/app/store/models/assesment.model";
import { StorageService } from "../storage-service/storage.service";
import { FirebaseService } from "../firebase-service/firebase.service";
import { OfflineAssessmentCompletionService } from "../offline-assessment-completion/offline-assessment-completion.service";
import { BehaviorSubject, firstValueFrom, map, retry, timeout } from "rxjs";
import { ToastService } from "../toast-service/toast.service";
import { TranslateService } from "@ngx-translate/core";

type ConnectionType = "wifi" | "cellular" | "none" | "unknown";

@Injectable({ providedIn: "root" })
export class NetworkService {
  private http = inject(HttpClient);
  private storage = inject(StorageService);
  private httpServices = inject(HttpService);
  private firebaseService = inject(FirebaseService);
  private offlineAssessmentCompletionService = inject(
    OfflineAssessmentCompletionService,
  );
  private toastService = inject(ToastService);
  private translate = inject(TranslateService);
  private networkStatus = new BehaviorSubject<boolean>(true);
  connectionType: ConnectionType = "unknown";

  constructor() {
    this.initializeNetworkStatus();
  }

  private async initializeNetworkStatus() {
    const status = await Network.getStatus();
    this.networkStatus.next(status.connected);

    Network.addListener("networkStatusChange", async (status) => {
      this.networkStatus.next(status.connected);
      this.connectionType = status.connectionType;
      if (status.connected) await this.processOfflineRequests();
    });
  }

  /** ✅ Use this in components to get the latest network status as observable */
  public onNetworkStatusChange() {
    return this.networkStatus.asObservable();
  }

  public getCurrentNetworkStatus(): boolean {
    return this.networkStatus.getValue();
  }

  private isProcessing = false;

  private async processOfflineRequests() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      await this.processPendingAssetImageSync();
      await this.processPendingImageUploads();

      let requests = (await this.storage.get("requests")) || [];
      let failedCount = 0;

      for (const request of requests) {
        const remaining = requests.filter(
          (_request: OfflineRequestModel) => _request !== request,
        );
        await this.storage.set("requests", remaining);

        try {
          await firstValueFrom(
            this.http.request(request.method, request.url, {
              body: request.body,
            }),
          );
          await this.removeSyncedAssetOverride(request.body);
        } catch (error) {
          console.error("Offline request failed (dropped):", error);
          failedCount++;
        }
        requests = remaining;
      }

      // if (failedCount > 0) {
      //   this.toastService.toastFailed(this.translate.instant("SYNC_FAILED"));
      // }
    } finally {
      this.isProcessing = false;
    }
  }

  private async removeSyncedAssetOverride(body: unknown): Promise<void> {
    const payload = body as {
      id?: string;
      assetId?: string;
      assessmentId?: string;
    } | null;

    const assessmentId = payload?.assessmentId || payload?.id;
    const assetId = payload?.assetId;

    if (!assessmentId || !assetId) return;

    await this.offlineAssessmentCompletionService.removeRecord(
      assessmentId,
      assetId,
    );
  }
  async processPendingAssetImageSync(): Promise<void> {
    const pendingImages: Record<
      string,
      { assessmentId: string; assetId: string; src: string }
    > = (await this.storage.get("LocalAssetImages")) || {};

    const remaining: Record<
      string,
      { assessmentId: string; assetId: string; src: string }
    > = { ...pendingImages };

    for (const [key, item] of Object.entries(pendingImages)) {
      try {
        const response: FileResponseModel = await firstValueFrom(
          this.httpServices.UploadImage({
            directory: "assessment",
            image: item.src,
          }),
        );

        if (!response?.data?.src) {
          throw new Error("No image data returned from API");
        }

        await firstValueFrom(
          this.firebaseService.uploadAssetImageToAssessment(
            response.data.src,
            item.assessmentId,
            item.assetId,
          ),
        );

        delete remaining[key];
      } catch (error) {
        console.error("Asset image re-upload failed:", error);
      }
    }

    await this.storage.set("LocalAssetImages", remaining);
  }

  async processPendingImageUploads(): Promise<void> {
    const uploadQueue: FileModel[] =
      (await this.storage.get("PendingImageUploads")) || [];

    const remainingQueue: FileModel[] = [];
    let requests = (await this.storage.get("requests")) || [];

    for (const item of uploadQueue) {
      try {
        const response = await firstValueFrom(
          this.httpServices.UploadImage({
            directory: item.directory,
            image: item.src,
          }),
        );

        if (!response?.data?.id) {
          throw new Error("No image data returned from API");
        }

        const uploadedFile = {
          id: response?.data?.id,
          src: response?.data?.src,
          fileType: response?.data?.fileType,
        };

        requests = requests.map((req: OfflineRequestModel) => {
          const clonedReq = structuredClone(req);
          if (
            item.directory === "finding" &&
            clonedReq.body?.info?.id === item?.unique_id
          ) {
            const attachments = clonedReq?.body?.info!.attachment ?? [];

            clonedReq.body.info!.attachment = [
              ...attachments.filter(
                (_attachment) => !_attachment.src?.startsWith("data:"),
              ),
              uploadedFile,
            ];
          }

          if (item.directory === "action" && clonedReq.body?.actions?.length) {
            clonedReq.body.actions = clonedReq.body.actions.map((action) => {
              if (action.id !== item.unique_id) return action;

              return {
                ...action,
                attachment: [
                  ...(action.attachment ?? []).filter(
                    (_attachment) => !_attachment.src?.startsWith("data:"),
                  ),
                  uploadedFile,
                ],
              };
            });
          }

          return clonedReq;
        });
      } catch (err) {
        console.error("Image re-upload failed:", err);
        remainingQueue.push(item);
      }
    }

    // Save updated requests back to storage
    await this.storage.set("requests", requests);

    // Save failed uploads back for retry
    await this.storage.set("PendingImageUploads", remainingQueue);
  }
}
